import React, { useEffect, useCallback, useMemo, useRef, useDeferredValue } from 'react';
import PropTypes from 'prop-types';
import CodeCopy from 'components/CodeCopy/CodeCopy.component';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary.component';
import CompatibleDisplay from './CompatibleDisplay/CompatibleDisplay.component';
import LightWeightMarkup from './LightweightMarkup/LightweightMarkup.component';
import './styles.scss'


const StoplightModal = React.lazy(() => import('components/APISpec/StoplightModal.component'))

function loadTocHTML(content){
  if(content.includes('<div id="toc')){
    return true
  }
  return false
}

function Article({ 
  article, 
  docType = '',
  isStoplight = false,
  fileName = null,
  isConnectorConfig = false,
  pageVersion = null,
  q = false
}){
  const title = article?.frontmatter?.title ?? 'No Title';
  const subtitle = article?.frontmatter?.subtitle ?? null;
  const productVersion = article?.frontmatter?.productVersion ?? null;
  const product = article?.frontmatter?.product ?? null;
  const appType = article?.frontmatter?.appType ?? null;

  const tocSet = useMemo(() => new Set(), [])
  const ref = useRef('')
  
  const deferredArticleHtml = useDeferredValue(article.html)
  const tocHTML = useMemo(() => {
    return loadTocHTML(deferredArticleHtml)
  }, [deferredArticleHtml])

  const handleObserver = useCallback((entries) => {
    if(article.frontmatter.permalink !== '2'){
      entries.forEach((entry) => {
        const id = entry.target.getAttribute('id');

        if (entry.intersectionRatio > 0.01) {

          if(ref.current && tocSet.size === 0){
            const tocCurrent = document.querySelector(`.toc li a[href="#${ref.current}"]`)
            tocCurrent?.classList.remove('active');
          }
          tocSet.add(id)

          const tocId = document.querySelector(`.toc li a[href="#${id}"]`)
          tocId?.classList.add('active');
        } else {
          tocSet.delete(id)

          ref.current = id
          if(tocSet.size !== 0){
            const tocId = document.querySelector(`.toc li a[href="#${id}"]`)
            tocId?.classList.remove('active');
          }
        }
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    
      const observer = new IntersectionObserver(handleObserver, {threshold: 0.01, rootMargin: "0px 0px -50px 0px"})

      // Track all sections that are added to the TOC by the asciidoc converter
      document.querySelectorAll('h2[id], h3[id]').forEach((section) => {
        observer.observe(section);
      });

      return () => {
        observer.disconnect()
      }
    
  }, []) // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <article style={{marginBottom: '2em'}} className={`${tocHTML || isStoplight ? 'article-margin' : ''} ${isStoplight ? 'has-stoplight' : ''} ${isConnectorConfig ? 'isConnectorConfig' : ''} ${q ? 'query' : '' }`} >

      <h1 className={`article-title-${docType}`}> 
        {title} 
        {subtitle && subtitle !== 'Release Notes' && (
          <span style={{display:'block'}} className="article-subtitle">
            {article.frontmatter.subtitle}
          </span>
        )}
      </h1>

      <CompatibleDisplay 
        product={product} 
        productVersion={productVersion} 
        appType={appType}
        fusionVersion={article?.frontmatter?.fusionVersion}
        connectorVersion={article?.frontmatter?.connectorVersion}
      />

      {isStoplight && <React.Suspense fallback="">
        <StoplightModal fileName={fileName} pageVersion={pageVersion}/>
      </React.Suspense> }
      <LightWeightMarkup 
        html={article.html} 
      />

      <ErrorBoundary message="copybtn">
        <CodeCopy />
      </ErrorBoundary>

    </article>
  );
};

Article.propTypes = {
  article: PropTypes.object.isRequired,
  docType: PropTypes.string,
  isStoplight: PropTypes.bool,
  fileName: PropTypes.string,
  isConnectorConfig: PropTypes.bool,
  pageVersion: PropTypes.string,
  q: PropTypes.bool
};

export default Article;

